import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Divider, Typography } from '@mui/material';
import HeadphonesOutlinedIcon from '@mui/icons-material/HeadphonesOutlined';

import {
  AssessmentStates,
  getRandomAudioCue, InterviewerStates,
  QuestionStates, QuestionsTypes,
} from './constant';
import useCodingStore from './CodingStore';
import CustomButton from 'components/CustomButton';
import MessageIndicator from 'components/MessageIndicator';
import WavRecorder from 'utils/WavRecorder';
import { analyseCodingExplanation, generateFollowup, skipCodeExplanation } from 'services';
import ActionTimerDialog from 'dialogs/ActionTimerDialog';
import { Session } from 'utils';
import MicTestAnimation from 'components/MicTestAnimation';
import TabSwitch from 'components/TabSwitch';
import { DisplayTimer } from './Timer';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    borderRight: '2px solid #D3D8DE',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  header: {
    padding: theme.spacing(2, 5),
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
    boxShadow: "0px 2px 12px 0px #0000001F"
  },
  content: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    overflow: 'auto'
  },
  codingProblem: {
    width: '100%',
    height: '52px',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.warning['clr-100'],
    borderRadius: theme.spacing(1)
  },
  card: {
    width: '75%',
    borderRadius: theme.spacing(2),
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  cardContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  divider: {
    height: '0.5px',
    width: '100%',
    backgroundColor: theme.palette.neutral['clr-200']
  },
  errorState: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.danger['clr-100'],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1, 2),
  },
  IntervieweeCardHeader: {
    display:'flex',
    alignItems: 'center', 
    justifyContent: 'space-between'
  }
}));

const QuestionSliderContent = [
  {
    id: 0, label: 'Q1',
    tooltip: <>Q1: Coding problem <br/>You have to code the solution for this given problem. <br/>You cannot switch directly!</>
  },
  {
    id: 1, label: 'F1',
    tooltip: <>F1: Follow up-1 question <br/>Interviewer will ask a follow up based on your response. <br/>You cannot switch directly!"</>
  },
  {
    id: 2, label: 'F2',
    tooltip: <>F2: Follow up-2 question <br/>Interviewer will ask a follow up based on your response. <br/>You cannot switch directly!"</>
  },
]

const QuestionTypesArray = Object.values(QuestionsTypes);

export const InterviewerCard = ({ active=false, loading = false, data = {} }) => {
  const classes = useStyles();

  const questionState = useCodingStore(state => state.questionState);

  return loading ? <MessageIndicator /> : (
    <>
      <Box className={classes.card}>
        <Box className={classes.IntervieweeCardHeader} >
          <Typography variant='h6-semiBold' color='primary.main'>Interviewer</Typography>
          {
            active &&
              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                { questionState !== QuestionStates.WAITING &&
                  <HeadphonesOutlinedIcon style={{ fontSize: '20px', color: '#8692A3' }}/>
                }
                <Typography variant='h6-medium' color='neutral.clr-400'>
                {
                  questionState === QuestionStates.WAITING
                    ? 'Interviewer is waiting...'
                    : 'Interviewer is listening...'
                }
                </Typography>
              </Box>
          }
        </Box>
        <Box className={classes.divider} />
        <Typography variant='h6-regular' style={{ lineHeight: '152%' }}>
          {data?.question}
        </Typography>
      </Box>
    </>
  );
}

const AnimatedEllipsis = ({ text = "Saving answer", interval = 500, maxDots = 3 }) => {
  const [dots, setDots] = React.useState(1);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setDots(prev => (prev % maxDots) + 1);
    }, interval);
    return () => clearInterval(timer);
  }, [interval, maxDots]);

  const ellipsis = '.'.repeat(dots) + '\u00A0'.repeat(maxDots - dots);

  return (
    <span
      style={{
        display: 'inline-block',
        minWidth: `${text.length + maxDots-2}ch`,
        fontFamily: 'inherit',
        textAlign: 'left',
        verticalAlign: 'middle',
      }}
    >
      {text}
      <span style={{ fontFamily: 'monospace' }}>{ellipsis}</span>
    </span>
  );
};

export const IntervieweeCard = ({
  active = false,
  questionType = QuestionsTypes.MAIN,
  nextQuesType = QuestionsTypes.FOLLOW_UP_1
}) => {
  const classes = useStyles();
  const wavRecorder = React.useRef(null);
  const recordStartTimeRef = React.useRef(0);
  const timeLeftRef = React.useRef(0);

  const [analysing, setAnalysing] = React.useState(false);
  const [autosubmitted, setAutosubmitted] = React.useState(false);
  const [recordStartTime, setRecordStartTime] = React.useState(0);
  const [openFinalSubmittionDialog, setOpenFinalSubmittionDialog] = React.useState(false);

  const sectionTimeData = useCodingStore(state => state.sectionTimeData);

  const [
    questionState, setQuestionState, timeLeft,
    setQuestionType, attempt, setTimeOut,
    setAssessmentState
  ] = useCodingStore(state => [
    state.questionState, state.setQuestionState, state.timeLeft,
    state.setQuestionType, state.attempt, state.setTimeOut,
    state.setAssessmentState
  ]);

  const [updateAnalysis, analysis] = useCodingStore(state => [
    state.updateAnalysis, state.analysis[questionType]
  ]);

  React.useEffect(() => {
    recordStartTimeRef.current = recordStartTime;
  }, [recordStartTime]);

  React.useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  const handleSkip = async () => {
    if (!active) return;
    try {
      await skipCodeExplanation({
        questionType,
        codeChallengeAttemptId: attempt?._id,
      });

      updateAnalysis({ explanation: '', skip: true, submitted: true });

      if (questionType === QuestionsTypes.FOLLOW_UP_2) {
        setTimeOut(true);
      } else {
        setQuestionState(QuestionStates.FETCHING_QUESTIONS);
        setQuestionType(nextQuesType);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRecordedAudio = React.useCallback(async (audioBlob) => {
    const duration = recordStartTimeRef.current - timeLeftRef.current;
    const analysisData = {
      audioBlob,
      questionType,
      codeChallengeAttemptId: attempt?._id,
      explainedAt: new Date(),
      skip: false,
      duration: duration,
      autosubmitted: autosubmitted
    }

    try {
      const handleEvent = (event, data) => {
        if (event === 'error') {
          setAnalysing(false);
          if(timeLeftRef.current) {
            setQuestionState(QuestionStates.WAITING);
          }else{
            handleSkip();
            setAutosubmitted(true);
          }
        } else if (event === 'progress') {
          setAnalysing(true);
        } else if (event === 'analysed') {
          updateAnalysis({ explanation: data?.transcript, ...data });
          setAnalysing(false);
          if (data?.analysis?.isError) {
            if(!timeLeftRef.current){
              setAutosubmitted(true);
              handleSkip();
            }
            setQuestionState(QuestionStates.WAITING);
            return;
          }
          if (questionType === QuestionsTypes.FOLLOW_UP_2) {
            setAssessmentState(AssessmentStates.FINISHING)
            // setTimeout(() => setOpenFinalSubmittionDialog(true), 2000);
          } else {
            setQuestionState(QuestionStates.FETCHING_QUESTIONS);
            updateAnalysis({ submitted: true });
            setQuestionType(nextQuesType);
          }
        }
      };

      analyseCodingExplanation(analysisData, handleEvent);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const startRecording = () => {
    wavRecorder.current = new WavRecorder();
    wavRecorder.current.start();
    setRecordStartTime(timeLeft);
    setQuestionState(QuestionStates.RECORDING);
  }

  const endRecording = () => {
    setQuestionState(QuestionStates.RECORDED);
    wavRecorder.current.stop(handleRecordedAudio);
    wavRecorder.current = null;
  }

  React.useEffect(() => {
    if (timeLeft > 0 || !active) return;

    if (questionState === QuestionStates.RECORDING) {
      endRecording();
    } else if(questionState=== QuestionStates.RECORDED && analysing){
      return;
    } else {
      setAutosubmitted(true);
      handleSkip();
    }
  }, [timeLeft]);

  return (
    <Box className={classes.cardContainer}>
      <Box className={classes.card}>
        <Box className={classes.IntervieweeCardHeader} >
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography variant='h6-semiBold' color='primary.main'>You</Typography>
            {
              questionState === QuestionStates.RECORDING && active &&
                <>
                  <Divider orientation="vertical" flexItem/>
                  <MicTestAnimation start={true} />
                </>
            }
          </Box>

          {
            active && sectionTimeData.showTimer &&
              <DisplayTimer
                timeLeft={timeLeft}
                warningTime={sectionTimeData.warningTime}
              />
          }
        </Box>
        <Box className={classes.divider} />
        {
          (analysis?.explanation && !analysis?.analysis?.isError) ?
            <Typography variant='h6-regular' style={{ lineHeight: '152%' }}>
              {analysis?.explanation}
            </Typography>
            :
            <Box>
              <Typography variant='body01-semiBold'>
                Your answer will be auto-saved after 
                {questionType === QuestionsTypes.MAIN ? " 3 " : " 2 "}
                minutes
              </Typography>
              <CustomButton
                disabled={analysis?.skip || analysing}
                sx={{ marginTop: '8px', width: '180px' }}
                size='medium'
                variant={questionState === QuestionStates.RECORDING ? 'outlined' : 'contained'}
                onClick={
                  questionState === QuestionStates.RECORDING
                    ? endRecording : startRecording
                }
              >
                {
                  analysing 
                    ? <AnimatedEllipsis text='Saving&nbsp;answer' />
                    : questionState === QuestionStates.RECORDING
                      ? "Save answer"
                      : analysis?.analysis?.isError
                        ? "Answer again" : "Answer now"
                }
              </CustomButton>
              {
                analysis?.skip ?
                  <Box className={classes.errorState}>
                    <Typography variant='body01-bold' color={'danger.clr-700'}>
                      The interviewer kept waiting, but you didn’t respond. Try to stay alert!
                    </Typography>
                  </Box>
                  :
                  analysis?.analysis?.isError ?
                    <Box className={classes.errorState}>
                      <Typography variant='body01-bold' color={'danger.clr-700'}>
                        Oops! Interviewer couldn’t hear you. Could you please try again?
                      </Typography>
                    </Box>
                    : null
              }
            </Box>
        }
      </Box>
      {
        openFinalSubmittionDialog &&
        <ActionTimerDialog
          open={openFinalSubmittionDialog}
          onClose={() => setAssessmentState(AssessmentStates.FINISHING)}
          title={`It was great talking to you, ${Session.user.name}`}
          timerMessage='Auto-submitting {timeLeft} seconds'
        />
      }
    </Box>
  );
}

const MainSection = ({ question, active = false, toSpeech = () => { }, audioRef }) => {
  const classes = useStyles();

  const [
    questionState, setQuestionState, startCoding, questionType,
    setInterviewerState, timeLeft, setTriggerHint, triggerHint
  ] = useCodingStore(state => [
    state.questionState, state.setQuestionState, state.startCoding, state.questionType,
    state.setInterviewerState, state.timeLeft, state.setTriggerHint, state.triggerHint
  ]);

  const [codeSubmitted, setCodeSubmitted] = React.useState(false);
  const [startRecording, setStartRecording] = React.useState(false);
  const [explanationText, setExplanationText] = React.useState('');
  const [timeOutWarning, setTimeOutWarning] = React.useState(false);

  const activeRef = React.useRef(active);
  const triggerHintRef = React.useRef(triggerHint);
  const codeSubmittedRef = React.useRef(codeSubmitted);
  const isDev = process.env.REACT_APP_STAGE === "dev";

  // Keep ref updated with the latest state
  React.useEffect(() => {
    codeSubmittedRef.current = codeSubmitted;
  }, [codeSubmitted]);

  React.useEffect(() => {
    activeRef.current = active;
  }, [active]);
  
  React.useEffect(() => {
    triggerHintRef.current = triggerHint;
  }, [triggerHint]);

  React.useEffect(() => {
    if (!activeRef.current) return;
    const audio = audioRef.current;
    if (!audio) return;

    const handleAudioEnd = () => {
      if (!activeRef.current) return;
      if (timeOutWarning) {
        setTimeOutWarning(false);
        setInterviewerState(InterviewerStates.LISTINING);
        return;
      }
      else if(triggerHintRef.current){
        setTriggerHint(false);
        setInterviewerState(InterviewerStates.LISTINING);
        return;
      }
      else if (codeSubmittedRef.current && !triggerHint && !timeOutWarning) {
        setQuestionState(QuestionStates.WAITING);
        setInterviewerState(InterviewerStates.LISTINING);
        setStartRecording(true);
      } else {
        startCoding();
      }
    };

    audio.addEventListener('ended', handleAudioEnd);

    return () => {
      audio.removeEventListener('ended', handleAudioEnd);
    };
  }, [audioRef]);

  React.useEffect(() => {
    if (!active) return;

    if (questionState === QuestionStates.CODE_SUBMITTED) {
      const cue = getRandomAudioCue("Main_Explanation_Cues");
      setExplanationText(cue.text);
      setCodeSubmitted(true);
      toSpeech(cue.audioUrl);
      setQuestionState(QuestionStates.INTERVIEWER_ASKING);
    }

    if (questionState === QuestionStates.INTERVIEWER_ASKING && !codeSubmitted) {
      toSpeech(getRandomAudioCue("Main_Intro_Cues").audioUrl);
    }
  }, [questionState, active]);

  React.useEffect(() => {
    if (!active || timeLeft === 0) return;

    if (questionState === QuestionStates.CODING) {
      if (timeLeft === 1380) {
        setTriggerHint(true);
        toSpeech(getRandomAudioCue("Main_Hint_Cues").audioUrl);
      }
      if (timeLeft === 600) {
        setTimeOutWarning(true);
        toSpeech(getRandomAudioCue("Main_Timeout1_Cues").audioUrl);
      }
      if (timeLeft === 60) {
        setTimeOutWarning(true);
        toSpeech(getRandomAudioCue("Main_Timeout2_Cues").audioUrl);
      }
    }
  }, [timeLeft, active]);

  return (
    <>
      <Box id='question-panel' className={classes.codingProblem}>
        <Typography id='question-panel-container' variant='h6-medium'>
          Coding Problem
        </Typography>
      </Box>

      {/* Question copy allowed only in dev env */}
      <Typography
        dangerouslySetInnerHTML={{ __html: question }}
        style={
          isDev
            ? undefined
            : {
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              pointerEvents: "none",
            }
        }
      />

      {
        codeSubmitted &&
        <InterviewerCard
          data={{ question: explanationText }}
          active={startRecording && questionType === QuestionsTypes.MAIN}
        />
      }
      {
        codeSubmitted && startRecording &&
        <IntervieweeCard active={startRecording && questionType === QuestionsTypes.MAIN} />
      }
    </>
  )
};

const FollowUpSection = ({
  active = false,
  toSpeech = () => { },
  audioRef,
  questionTypeIndex = 1
}) => {
  const questionType = QuestionTypesArray[questionTypeIndex];
  const previousQuesType = QuestionTypesArray[questionTypeIndex - 1];
  const nextQuesType = QuestionTypesArray[questionTypeIndex + 1];

  const [
    questionState, setQuestionState, startCoding, activeQuestionType,
    updateAnalysis, analysis, attempt, timeLeft,
    challenge, allSectionAnalysis, setInterviewerState, setTriggerHint,
    triggerHint
  ] = useCodingStore((state) => ([
    state.questionState, state.setQuestionState, state.startCoding, state.questionType,
    state.updateAnalysis, state.analysis[questionType], state.attempt, state.timeLeft,
    state.challenge, state.analysis, state.setInterviewerState, state.setTriggerHint,
    state.triggerHint
  ]));

  const [loading, setLoading] = React.useState(false);
  const [codeSubmitted, setCodeSubmitted] = React.useState(false);
  const [startRecording, setStartRecording] = React.useState(false);
  const [explanationText, setExplanationText] = React.useState('');
  const [timeOutWarning, setTimeOutWarning] = React.useState(false);

  const activeRef = React.useRef(active);
  const triggerHintRef = React.useRef(triggerHint);
  const codeSubmittedRef = React.useRef(false);

  // Keep ref updated with the latest state
  React.useEffect(() => {
    codeSubmittedRef.current = codeSubmitted;
  }, [codeSubmitted]);

  React.useEffect(() => {
    activeRef.current = active;
  }, [active]);

  React.useEffect(() => {
    triggerHintRef.current = triggerHint;
  }, [triggerHint]);

  React.useEffect(() => {
    if(!activeRef.current) return;
    const audio = audioRef.current;
    if (!audio) return;

    const handleAudioEnd = () => {
      if (!activeRef.current) return;
      if (timeOutWarning) {
        setTimeOutWarning(false);
        setInterviewerState(InterviewerStates.LISTINING);
        return;
      }
      else if(triggerHintRef.current){
        setTriggerHint(false);
        setInterviewerState(InterviewerStates.LISTINING);
        return;
      }
      else if (codeSubmittedRef.current && !triggerHint && !timeOutWarning) {
        setQuestionState(QuestionStates.WAITING);
        setInterviewerState(InterviewerStates.LISTINING);
        setStartRecording(true);
      } else {
        startCoding();
      }
    };

    audio.addEventListener('ended', handleAudioEnd);

    return () => {
      audio.removeEventListener('ended', handleAudioEnd);
    };
  }, [audioRef]);

  const fetchQuestion = async () => {
    if (analysis?.question) return;
    setLoading(true);

    try {
      const { followupQestion } = await generateFollowup(challenge._id, {
        sourceCode: allSectionAnalysis[previousQuesType].sourceCode,
        codeChallengeAttemptId: attempt._id,
        answer: allSectionAnalysis[previousQuesType]?.explanation,
        questionType,
      });

      updateAnalysis(followupQestion);
      setQuestionState(QuestionStates.INTERVIEWER_ASKING);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (!active) return;

    switch (questionState) {
      case QuestionStates.FETCHING_QUESTIONS:
        fetchQuestion();
        break;

      case QuestionStates.CODE_SUBMITTED:
        const cue = getRandomAudioCue("Followup_Explanation_Cues")
        setExplanationText(cue.text);
        setCodeSubmitted(true);
        toSpeech(cue.audioUrl);
        setQuestionState(QuestionStates.INTERVIEWER_ASKING);
        break;

      case QuestionStates.INTERVIEWER_ASKING:
        if (!codeSubmitted) {
          toSpeech(getRandomAudioCue("Followup_Intro_Cues").audioUrl);
        }
        break;

      default:
        break;
    }
  }, [questionState, active, codeSubmitted, analysis]);

  React.useEffect(() => {
    if (!active || timeLeft === 0) return;

    if (questionState === QuestionStates.CODING) {
      if (timeLeft === 240) {
        setTriggerHint(true);
        toSpeech(getRandomAudioCue("Followup_Hint_Cues").audioUrl);
      }
      if (timeLeft === 60) {
        setTimeOutWarning(true);
        toSpeech(getRandomAudioCue("Followup_Timeout_Cues").audioUrl);
      }
    }
  }, [timeLeft, active]);

  return (
    <>
      <InterviewerCard loading={loading} data={analysis} />

      {
        codeSubmitted &&
        <InterviewerCard
          data={{ question: explanationText }}
          active={startRecording && activeQuestionType === questionType}
        />
      }
      {
        codeSubmitted && startRecording && (
          <IntervieweeCard
            questionType={questionType}
            nextQuesType={nextQuesType}
            active={startRecording && activeQuestionType === questionType}
          />
        )}
    </>
  );
};

const MiddleSection = () => {
  const classes = useStyles();
  const audioRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const [challenge, questionType, setInterviewerState] = useCodingStore(state => [
    state.challenge, state.questionType, state.setInterviewerState
  ]);

  const [followUpSections, setFollowUpSections] = React.useState([]);

  const toSpeech = React.useCallback(async (audioUrl = '') => {
    try {
      const audio = audioRef.current;
      if (!audio) throw new Error("Audio element not found");

      audio.onerror = null;

      const playPromise = new Promise((resolve, reject) => {
        audio.onerror = () => {
          reject(new Error("Audio failed to load"));
        };
        audio.onloadeddata = () => {
          audio.onerror = null;
          resolve();
        };
      });

      audio.src = audioUrl;
      await playPromise;

      audio.onplay = () => {
        setInterviewerState(InterviewerStates.SPEAKING);
      };

      await audio.play()
    } catch (error) {
      console.log(error);
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current.dispatchEvent(new Event('ended'));
    }
  }, []);

  React.useEffect(() => {
    setFollowUpSections(prev =>
      prev.includes(questionType) ? prev : [...prev, questionType]
    );
  }, [questionType]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      container.scrollTop = container.scrollHeight;
    });

    observer.observe(container, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  return (
    <Box className={classes.root}>
      <audio ref={audioRef} preload='auto'></audio>

      <Box className={classes.header}>
        <TabSwitch
          width="230px"
          height={48}
          disabled={true}
          options={QuestionSliderContent}
          value={ Object.values(QuestionsTypes).indexOf(questionType) }
        />
      </Box>
      <Box className={classes.content} ref={containerRef}>
        <MainSection
          question={challenge.description}
          active={questionType === QuestionsTypes.MAIN}
          toSpeech={toSpeech}
          audioRef={audioRef}
        />

        {[
          QuestionsTypes.FOLLOW_UP_1,
          QuestionsTypes.FOLLOW_UP_2
        ].map(type =>
          followUpSections.includes(type) && (
            <FollowUpSection
              key={type}
              active={questionType === type}
              toSpeech={toSpeech}
              audioRef={audioRef}
              questionTypeIndex={QuestionTypesArray.indexOf(type)}
            />
          )
        )}
      </Box>
    </Box>
  );
};

export default MiddleSection;